import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Copy } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import { api, formatApiErrorDetail } from "../lib/api";
import { CONTACT, TOPICS } from "../lib/content";

const CopyRow = ({ value, testid }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (e) {
      toast.error("Copy failed — please write it down.");
    }
  };
  return (
    <div className="flex items-center gap-3" data-testid="copy-row">
      <code className="rounded-lg bg-muted px-3 py-2 font-mono text-sm select-all">
        {value}
      </code>
      <button
        type="button"
        onClick={copy}
        aria-label={`Copy ${value}`}
        className="btn btn-ghost btn-sm !rounded-full !border-border"
        data-testid={testid}
      >
        {copied ? (
          <CheckCircle2 className="h-4 w-4" />
        ) : (
          <Copy className="h-4 w-4" />
        )}
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
};

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    topic: TOPICS[0],
    message: "",
    website: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (
      !form.name.trim() ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) ||
      !form.message.trim()
    ) {
      setErr("Please add your name, a valid email, and a message.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Mahalo! Your message was received.");
    } catch (ex) {
      setErr(formatApiErrorDetail(ex.response?.data?.detail));
    }
    setBusy(false);
  };

  return (
    <>
      <PageHero
        eyebrow="Contact"
        lines={[
          { text: "Talk story" },
          { text: "with us", className: "font-serifg italic font-semibold" },
        ]}
        lead="Questions, resources to share, donated services, or media requests."
      />

      <section className="wrap py-16 sm:py-20">
        <div className="grid gap-10 lg:grid-cols-2">
          <Reveal>
            {done ? (
              <div
                className="card-surface border-primary/40"
                data-testid="contact-success-card"
              >
                <CheckCircle2 className="h-10 w-10 text-primary dark:text-accent" />
                <h2 className="font-display text-2xl font-bold">
                  Mahalo, {form.name.split(" ")[0]}!
                </h2>
                <p className="text-muted-foreground">
                  Your message was received. We read everything and reply as
                  soon as we can.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} noValidate className="flex flex-col gap-4" data-testid="contact-form">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="flex flex-col gap-1.5">
                    <label className="label" htmlFor="c-name">Name</label>
                    <input id="c-name" className="field" type="text" autoComplete="name" required value={form.name} onChange={set("name")} data-testid="contact-name-input" />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="label" htmlFor="c-email">Email</label>
                    <input id="c-email" className="field" type="email" autoComplete="email" required value={form.email} onChange={set("email")} data-testid="contact-email-input" />
                  </div>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="label" htmlFor="c-topic">Topic</label>
                  <select id="c-topic" className="field" value={form.topic} onChange={set("topic")} data-testid="contact-topic-select">
                    {TOPICS.map((t) => (
                      <option key={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="label" htmlFor="c-msg">Message</label>
                  <textarea id="c-msg" className="field min-h-[140px]" required value={form.message} onChange={set("message")} data-testid="contact-message-input" />
                </div>
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  value={form.website}
                  onChange={set("website")}
                  className="pointer-events-none absolute left-[-9999px] h-0 w-0 opacity-0"
                />
                <p className="text-sm text-muted-foreground">
                  Please don't include ID numbers, tax returns, leases, or
                  financial records.
                </p>
                {err && (
                  <p className="text-sm font-semibold text-destructive" role="alert" data-testid="contact-form-error">
                    {err}
                  </p>
                )}
                <button className="btn w-fit" type="submit" disabled={busy} data-testid="contact-form-submit">
                  {busy ? "Sending…" : "Send message"}
                </button>
              </form>
            )}
          </Reveal>

          <Reveal delay={0.12} className="flex flex-col gap-5">
            <div className="card-surface">
              <p className="eyebrow">Kamalama Foundation</p>
              <CopyRow value={CONTACT.email} testid="copy-email-button" />
              <CopyRow value={CONTACT.phone} testid="copy-phone-button" />
              <p className="text-muted-foreground">
                3-4684 Kuhio Highway
                <br />
                Lihue, HI 96766
              </p>
            </div>
            <div className="callout">
              <b className="font-display">Prefer not to use forms?</b>
              <p className="text-muted-foreground">
                Email or call us any time. Our phone number and mailing
                address are in the footer of every page.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
